import type { TokenResponse } from '@/types/api';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '@/lib/tokenStorage';
import { ensureMediaSession, revokeMediaSession, MediaSessionError } from './media-session';
import { normalizePrivateMediaReferences } from '@/lib/media-url';

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); this.name = 'ApiError'; }
}

/**
 * Phase 4 부터 cctv-web 은 두 백엔드를 호출한다:
 *   - `getAuthApiUrl()` (VITE_AUTH_API_URL) — auth/users/sites (ontoh-auth-api)
 *   - `getApiUrl()`     (VITE_API_URL)      — cctv 도메인 (ontoh-cctv-api)
 *
 * 401 발생 시 어느 API 쪽이든 동일한 access token 을 쓰므로 auth-api 의
 * `/v1/auth/refresh` 로 갱신 후 원 요청 재시도. 토큰 저장은 `lib/tokenStorage`.
 */

export function getApiUrl(): string {
  const url = import.meta.env.VITE_API_URL;
  return typeof url === 'string' ? url : '';
}

export function getAuthApiUrl(): string {
  const url = import.meta.env.VITE_AUTH_API_URL;
  if (typeof url === 'string' && url.length > 0) return url;
  // 개발 기본값 — Phase 2 portal/admin 과 동일
  return 'http://localhost:8001';
}

interface RefreshFlight {
  accessToken: string | null;
  refreshToken: string;
  promise: Promise<string | null>;
}
let refreshFlight: RefreshFlight | null = null;
let completedRefresh: {
  previousAccess: string | null; previousRefresh: string;
  accessToken: string; refreshToken: string;
} | null = null;

function sameStoredSession(accessToken: string | null, refreshToken: string | null) {
  return getAccessToken() === accessToken && getRefreshToken() === refreshToken;
}

export function isCurrentAuthSession(accessToken: string | null, refreshToken: string | null): boolean {
  return sameStoredSession(accessToken, refreshToken) ||
    (completedRefresh?.previousAccess === accessToken && completedRefresh.previousRefresh === refreshToken &&
      sameStoredSession(completedRefresh.accessToken, completedRefresh.refreshToken));
}

async function attemptRefresh(accessToken: string | null, refreshToken: string | null): Promise<string | null> {
  if (completedRefresh?.previousAccess === accessToken &&
    completedRefresh.previousRefresh === refreshToken &&
    sameStoredSession(completedRefresh.accessToken, completedRefresh.refreshToken)) {
    return completedRefresh.accessToken;
  }
  if (!refreshToken || !sameStoredSession(accessToken, refreshToken)) return null;
  if (refreshFlight?.refreshToken === refreshToken && refreshFlight.accessToken === accessToken) {
    return refreshFlight.promise;
  }

  const flight: RefreshFlight = { accessToken, refreshToken, promise: Promise.resolve(null) };
  flight.promise = (async () => {
    try {
      const res = await fetch(`${getAuthApiUrl()}/v1/auth/refresh`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as TokenResponse;
      // A completed refresh must never restore a logged-out or replaced session.
      if (!sameStoredSession(accessToken, refreshToken)) return null;
      setTokens(data.access_token, data.refresh_token);
      // REST authentication remains usable when this browser cannot bind media.
      await ensureMediaSession(getApiUrl(), data.access_token).catch(() => {});
      if (!sameStoredSession(data.access_token, data.refresh_token)) return null;
      completedRefresh = { previousAccess: accessToken, previousRefresh: refreshToken,
        accessToken: data.access_token, refreshToken: data.refresh_token };
      return data.access_token;
    } finally {
      if (refreshFlight === flight) refreshFlight = null;
    }
  })();
  refreshFlight = flight;
  return flight.promise;
}

function clearAuthAndRedirect(): void {
  void revokeMediaSession(getApiUrl()).catch(() => {});
  clearTokens();
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

export async function ensureCurrentMediaSession(): Promise<void> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken) throw new ApiError('로그인이 필요합니다.', 401);
  try {
    await ensureMediaSession(getApiUrl(), accessToken);
  } catch (error) {
    if (error instanceof MediaSessionError && error.status === 401) {
      const refreshed = await attemptRefresh(accessToken, refreshToken);
      if (refreshed) {
        await ensureMediaSession(getApiUrl(), refreshed);
        return;
      }
      if (sameStoredSession(accessToken, refreshToken)) clearAuthAndRedirect();
      throw new ApiError('Session expired or changed. Please log in again.', 401);
    }
    throw error;
  }
}

async function _fetch<T>(
  baseUrl: string,
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const url = `${baseUrl}${endpoint}`;
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const newToken = await attemptRefresh(accessToken, refreshToken);
    if (!newToken) {
      if (sameStoredSession(accessToken, refreshToken)) clearAuthAndRedirect();
      throw new ApiError('Session expired or changed. Please log in again.', 401);
    }
    headers['Authorization'] = `Bearer ${newToken}`;
    response = await fetch(url, { ...options, headers });
  }

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        errorMessage =
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
      }
    } catch {
      // ignore parse error
    }
    if (response.status === 403 && !endpoint.startsWith('/api/v1/access/') && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cctv:permission-denied'));
    }
    throw new ApiError(errorMessage, response.status);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

/** cctv-api (`VITE_API_URL`) 호출. 카메라·이벤트·알람·에지 등 도메인. */
export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const data = await _fetch<T>(getApiUrl(), endpoint, { ...options, credentials: 'include' });
  return normalizePrivateMediaReferences(data, getApiUrl());
}

/** auth-api (`VITE_AUTH_API_URL`) 호출. auth · users · sites. */
export function fetchAuthApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return _fetch<T>(getAuthApiUrl(), endpoint, options);
}
